"use client"

import { useState } from "react"
import { useSelector, useDispatch } from "react-redux";

import { sendPublicToken, getPublicGasEstimate, resetErrorState } from "../redux/slices/walletSlice";
import TokenDisplay from "./shield/components/TokenDisplay";
import AmountInput from "./shield/components/AmountInput";
import { SuccessIconSvg } from "../assets/svg";
import { useCallback, useEffect } from "react";

function SendModal({ onClose, selectedToken }) {
  const dispatch = useDispatch();
  const [step, setStep] = useState(1)
  const [amount, setAmount] = useState("")
  const [useMaxAmount, setUseMaxAmount] = useState(false)
  const [recipient, setRecipient] = useState("")
  const [showErrors, setShowErrors] = useState(false)
  const loading = useSelector(state => state.wallet.loading);
  const txHash = useSelector(state => state.wallet.txHash);
  const error = useSelector(state => state.wallet.error);
  // gas estimate object provided by the wallet slice (shape depends on backend)
  const publicGasEstimate = useSelector(state => state.wallet.publicGasEstimate);
  const publicGasEstimateLoading = useSelector(state => state.wallet.publicGasEstimateLoading);
  const publicGasEstimateError = useSelector(state => state.wallet.publicGasEstimateError);

  const handleAmountChange = useCallback((value) => {
    setAmount(value)
    setUseMaxAmount(false)
  }, [])

  const handleMaxClick = useCallback(() => {
    if (selectedToken?.balance) {
      setAmount(selectedToken.balance)
      setUseMaxAmount(true)
    }
  }, [selectedToken])

  const getAmountError = useCallback(() => {
    const numAmount = parseFloat(amount)
    const numBalance = parseFloat(selectedToken?.balance || 0)

    if (!amount || amount === "") return 'Amount is required'
    if (numAmount <= 0) return 'Amount must be greater than 0'
    if (numAmount > numBalance) return 'Amount exceeds available balance'
    if (numAmount < 0) return 'Amount cannot be negative'
    return null
  }, [amount, selectedToken])

  const isValidAmount = useCallback(() => {
    const numAmount = parseFloat(amount)
    const numBalance = parseFloat(selectedToken?.balance || 0)
    return amount && numAmount > 0 && numAmount <= numBalance
  }, [amount, selectedToken])

  useEffect(() => {
    if (selectedToken && useMaxAmount) {
      setAmount(selectedToken.balance || '0')
    }
  }, [selectedToken, useMaxAmount])

  const handleNext = () => {
    const amountError = getAmountError();

    if (step === 1 && amountError) {
      setShowErrors(true);
      return;
    }

    // compute next step and then perform side-effects based on that transition
    const next = step < 4 ? step + 1 : step;
    // If we're moving from step 2 -> 3 (after recipient provided), request gas estimate
    if (step === 2 && next === 3) {
      dispatch(getPublicGasEstimate({ tokenId: selectedToken.id, amount, recipientAddress: recipient }));
    }
    setStep(next);
    setShowErrors(false);
  }

  const handleBack = () => {
    // make all error messages disappear when going back
    dispatch(resetErrorState());
    if (step > 1) setStep(step - 1)
  }

  const confirmPayment = async () => {
    const result = await dispatch(sendPublicToken({ tokenId: selectedToken.id, amount, recipientAddress: recipient }));
    if (result.type.match(sendPublicToken.fulfilled)) {
      setStep(4);
    } else {
      // Handle error case if needed
    }

  }
  console.log(publicGasEstimateError);

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="modal-step">
            <TokenDisplay selectedToken={selectedToken} actionText="Send" />
            <AmountInput
              amount={amount}
              useMaxAmount={useMaxAmount}
              selectedToken={selectedToken}
              onAmountChange={(val) => {
                handleAmountChange(val);
                if (showErrors) setShowErrors(false);
              }}
              onMaxClick={handleMaxClick}
              getAmountError={() => (showErrors ? getAmountError() : null)}
            />
          </div>
        )
      case 2:
        return (
          <div className="modal-step">
            {error && (
              <div className="error-alert mb-3">
                <strong>Error:</strong> {error}
              </div>
            )}
            <div className="form-group">
              <label>Recipient Address</label>
              <input
                placeholder="0x..."
                value={recipient}
                onChange={(e) => {
                  setRecipient(e.target.value);
                  if (showErrors) setShowErrors(false);
                }}
                className="form-input form-control"
              />
            </div>
            <div className="help-text text-sm text-gray-500 mt-2">Enter the recipient address and click Next to estimate network fees.</div>
          </div>
        )
      case 3:
        return (
          <div className="modal-step">
            {publicGasEstimateError && (
              <div className="error-alert mb-3 alert alert-danger">
                <strong>Error:</strong> {publicGasEstimateError}
              </div>
            )}
            {error && (
              <div className="error-alert mb-3 alert alert-danger">
                <strong>Error:</strong> {error}
              </div>
            )}
            <div className="transaction-summary card">
              <div className="card-body p-2">
                <h4 className="fw-bold mb-3">Transaction Summary</h4>
                <div className="d-flex justify-content-between py-2 border-bottom">
                  <span>Token:</span>
                  <span className="fw-bold">{selectedToken.name} ({selectedToken.symbol})</span>
                </div>
                <div className="d-flex justify-content-between py-2 border-bottom">
                  <span>Amount:</span>
                  <span className="fw-bold">{amount} {selectedToken.symbol}</span>
                </div>
                <div className="d-flex justify-content-between py-2 border-bottom">
                  <span>Recipient:</span>
                  <span className="fw-bold text-break ms-4 text-end">{recipient}</span>
                </div>
                <div className="d-flex justify-content-between py-2">
                  <span>Network Fee:</span>
                  <span className="fw-bold text-accent">
                    {
                      publicGasEstimateLoading ? 'Calculating...' : (
                        ` ${publicGasEstimate ? `${publicGasEstimate} WEI` : 'N/A'}`
                      )
                    }
                  </span>
                </div>
              </div>
            </div>
          </div>
        )
      case 4:
        return (
          <div className="modal-step success text-center py-4">
            <div className="mb-2 flex items-center justify-center text-center">
              <SuccessIconSvg width={70} height={70} />
            </div>
            <h3 className="fw-bold mb-2">Transaction Submitted!</h3>
            <p className="text-muted mb-4">Your transaction has been submitted to the network.</p>
            <div className="alert alert-info d-flex align-items-start text-start" role="alert">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-envelope-check me-2 mt-1 flex-shrink-0" viewBox="0 0 16 16">
                <path d="M2 2a2 2 0 0 0-2 2v8.01A2 2 0 0 0 2 14h5.5a.5.5 0 0 0 0-1H2a1 1 0 0 1-.966-.741l5.64-3.471L8 9.583l7-4.2V8.5a.5.5 0 0 0 1 0V4a2 2 0 0 0-2-2zm3.708 6.208L1 11.105V5.383zM1 4.217V4a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v.217l-7 4.2z"/>
                <path d="M16 12.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0m-1.993-1.679a.5.5 0 0 0-.686.172l-1.17 1.95-.547-.547a.5.5 0 0 0-.708.708l.774.773a.75.75 0 0 0 1.174-.144l1.335-2.226a.5.5 0 0 0-.172-.686"/>
              </svg>
              <div>
                <strong className="d-block mb-1">Email Notification</strong>
                <small>You will receive an email notification once your transaction is confirmed on the blockchain.</small>
              </div>
            </div>
          </div>
        )
      default:
        return null
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="d-flex align-items-center gap-3">
            <h2 className="m-0">Send Tokens to External Wallet</h2>
            {step < 4 && (
              <span className="badge bg-light text-dark border fw-normal" style={{ fontSize: '0.85rem', padding: '0.5rem 0.75rem' }}>
                Step {step} of 3
              </span>
            )}
          </div>
          <button className="close-button" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* <div className="step-indicator">
          {[1, 2, 3].map((i) => (
            <div key={i} className="step-indicator-item">
              <div className={`step-number ${i <= step ? "active" : ""}`}>{i}</div>
              {i < 3 && <div className={`step-line ${i < step ? "active" : ""}`} />}
            </div>
          ))}
        </div> */}

        {renderStep()}

        <div className="modal-actions">
          {step > 1 && (
            <button className="modal-button secondary" onClick={handleBack}>
              Back
            </button>
          )}
          {
            step === 3 ? (
              <button className="modal-button primary"
                onClick={() => confirmPayment()}
                disabled={loading || !amount || !recipient}
              >
                {loading ? "Processing..." : "Confirm"}
              </button>
            ) : (
              // Next button: disable on step 1 if amount missing, on step 2 if recipient missing
              <button
                className="modal-button primary"
                onClick={step === 4 ? onClose : handleNext}
              // removed disabled to show validation errors on click
              // disabled={(step === 1 && !isValidAmount()) || (step === 2 && !recipient)}
              >
                {step === 4 ? "Close" : "Next"}
              </button>
            )
          }
        </div>
      </div>
    </div>
  )
}

export default SendModal
